import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { CheckCircle2, XCircle, Loader2, HeartPulse, ShieldCheck } from "lucide-react";

/** Public certificate verification page. Path: /verify/cert/:attemptId
 * Reads /api/certificate/verify/{attemptId} which returns non-sensitive metadata.
 */
export default function CertificateVerify() {
  const { id } = useParams();
  const [state, setState] = useState({ loading: true, data: null, err: "" });

  useEffect(() => {
    api.get(`/certificate/verify/${id}`)
      .then(r => setState({ loading: false, data: r.data, err: "" }))
      .catch(e => setState({ loading: false, data: null, err: e?.response?.data?.detail || "Certificate not found" }));
  }, [id]);

  if (state.loading) {
    return <div className="min-h-screen grid place-items-center bg-slate-50"><Loader2 className="h-8 w-8 animate-spin text-sky-500"/></div>;
  }
  if (state.err || !state.data) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50 p-6">
        <Card className="max-w-md w-full border-red-200 rounded-3xl">
          <CardContent className="p-8 text-center space-y-3">
            <XCircle className="h-12 w-12 text-red-500 mx-auto"/>
            <div className="font-display font-black text-2xl text-slate-900">Not Verified</div>
            <p className="text-slate-500">{state.err || "This certificate could not be verified."}</p>
          </CardContent>
        </Card>
      </div>
    );
  }
  const d = state.data;
  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50 to-white p-4 sm:p-8">
      <div className="max-w-md mx-auto">
        <div className="text-center py-6">
          <div className="inline-flex items-center gap-2 rounded-full bg-white shadow-sm border border-slate-200 px-4 py-1.5">
            <HeartPulse className="h-4 w-4 text-sky-500"/>
            <span className="text-xs uppercase tracking-[0.2em] text-slate-500 font-bold">MedLearn · Certificate Verification</span>
          </div>
        </div>

        <Card className="border-emerald-200 rounded-3xl overflow-hidden" data-testid="cert-verify-card">
          <div className="h-2 bg-emerald-500"/>
          <CardContent className="p-6 text-center space-y-4">
            <CheckCircle2 className="h-14 w-14 text-emerald-500 mx-auto"/>
            <div>
              <div className="font-display font-black text-2xl text-slate-900">Verified Authentic</div>
              <div className="text-xs text-slate-500 mt-1">This certificate was issued by MedLearn.</div>
            </div>
            <div className="text-left border-t border-slate-100 pt-4 space-y-2">
              <Row label="Name" value={d.employee_name}/>
              <Row label="Employee ID" value={d.emp_id} mono/>
              <Row label="Training" value={d.training_title}/>
              <Row label="Score" value={`${d.score_pct}%`}/>
              <Row label="Issued On" value={d.issued_on}/>
              <Row label="Certificate ID" value={d.cert_id} mono/>
            </div>
            <div className="pt-2 flex items-center justify-center gap-1.5 text-xs text-emerald-600">
              <ShieldCheck className="h-3 w-3"/>Secure verification · read-only
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function Row({ label, value, mono }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <div className="text-xs uppercase tracking-wider text-slate-500 font-bold">{label}</div>
      <div className={`text-slate-900 font-medium ${mono ? "font-mono text-sm" : ""}`}>{value}</div>
    </div>
  );
}
